/**
 * Navigation helper utilities for consistent routing throughout the application
 */

import type { Notification as StoreNotification } from '@/lib/stores/notifications/types';
import type { NotificationResponse as ApiNotificationResponse } from '@/lib/shared-types/api/endpoints/notifications';

type NotificationLike = Pick<StoreNotification, 'type' | 'data'> | Pick<ApiNotificationResponse, 'type' | 'data'>;

/**
 * Get navigation path for a notification based on its type and data.
 * Handles both the API NotificationType values and the store Notification types.
 * @param notification - The notification object
 * @returns Navigation path string
 */
export function getNotificationNavigation(notification: NotificationLike): string {
  // Priority 1: Use explicit actionUrl if the backend provided one
  if (notification.data?.actionUrl) {
    return notification.data.actionUrl;
  }

  const streamUrl = notification.data?.streamId ? `/streams/${notification.data.streamId}` : '/streams';
  const commentUrl =
    notification.data?.streamId && notification.data?.commentId
      ? `/streams/${notification.data.streamId}#comment-${notification.data.commentId}`
      : notification.data?.streamId
        ? `/streams/${notification.data.streamId}`
        : '/comments';

  // Priority 2: Navigate based on type and contextual data
  switch (notification.type) {
    // ── Stream notifications ──────────────────────────────────────────────
    case 'stream_live':
    case 'stream_scheduled':  // store type
    case 'stream_ended':
      return streamUrl;

    // ── Comment notifications ─────────────────────────────────────────────
    // Backend sends 'comment'; store may use granular variants.
    case 'comment':
    case 'comment_reply':
    case 'comment_mention':
    case 'comment_liked':
      return commentUrl;

    // ── Mention / like notifications ──────────────────────────────────────
    // Backend sends 'mention' and 'like'; navigate to the relevant stream.
    case 'mention':
      return commentUrl;

    case 'like':
      return commentUrl;

    // ── Invite / access notifications ─────────────────────────────────────
    // Backend sends 'invite'; store may use 'invite_received'.
    case 'invite':
    case 'invite_received':
    case 'stream_invite':
      if (notification.data?.inviteCode) {
        return `/join/${notification.data.inviteCode}`;
      }
      return streamUrl;

    case 'access_granted':
    case 'access_denied':
      return streamUrl;

    // ── Admin / moderation notifications ─────────────────────────────────
    case 'moderation_action':
      return '/admin/moderation';

    // ── System notifications ───────────────────────────────────────────────
    case 'system':
    case 'system_announcement':
      return '/notifications';

    // ── Account / social notifications ────────────────────────────────────
    case 'account_security':
      return '/settings';

    case 'role_changed':
      return '/settings';

    case 'friend_joined':
      return '/profile';

    default:
      // Unknown type — fall back to the notification center so every item is clickable.
      return '/notifications';
  }
}

/**
 * Navigate to user profile page
 * @param userId - The user ID to navigate to
 * @param isAdmin - Whether the current user is an admin (defaults to true for admin context)
 * @returns Navigation path string
 */
export function navigateToUserProfile(userId: number | string | undefined, isAdmin: boolean = true): string | null {
  if (!userId) {
    return null;
  }
  
  // In admin context, always use admin user detail page
  if (isAdmin) {
    return `/admin/users/${userId}`;
  }
  
  // For non-admin context, could navigate to public profile or admin page
  // Defaulting to admin page for now
  return `/admin/users/${userId}`;
}

/**
 * Navigate to stream page with optional comment anchor
 * @param streamId - The stream ID
 * @param commentId - Optional comment ID to scroll to
 * @returns Navigation path string
 */
export function navigateToStream(streamId: number | string | undefined, commentId?: number | string): string | null {
  if (!streamId) {
    return '/streams';
  }
  
  if (commentId) {
    return `/streams/${streamId}#comment-${commentId}`;
  }
  
  return `/streams/${streamId}`;
}

/**
 * Get navigation path for user activity timeline items
 * @param activityType - The type of activity
 * @param metadata - Activity metadata containing streamId, commentId, etc.
 * @returns Navigation path string or null if no navigation should occur
 */
export function getActivityNavigation(
  activityType: string,
  metadata?: Record<string, unknown>
): string | null {
  switch (activityType) {
    case 'stream_view':
      return navigateToStream(metadata?.streamId as number | string | undefined);
    
    case 'comment':
      return navigateToStream(
        metadata?.streamId as number | string | undefined,
        metadata?.commentId as number | string | undefined
      );
    
    case 'like':
    case 'share':
      return navigateToStream(metadata?.streamId as number | string | undefined);
    
    case 'login':
    case 'invite_used':
      // No navigation for these activity types
      return null;
    
    default:
      return null;
  }
}
